import { useCallback, useEffect, useRef } from 'react';

import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';

import { loadTripInfo } from '@/features/uplift/adapter';
import { FiltersPriceType } from '@/infra/types/common/filters';

type TProps = {
  'aria-label'?: string;
  ariaHidden?: boolean;
  children: React.ReactNode;
  className?: string;
  isCruiseOptionPrice?: boolean;
  isLightboxDisabled?: boolean;
  isTaxesIncluded?: boolean;
  isTooltipDisabled?: boolean;
  onReady?: (data: string) => void;
  price: number;
  priceType?: FiltersPriceType;
  tripInfo?: Record<string, unknown>;
};

const UpliftRoot = ({
  'aria-label': ariaLabel,
  ariaHidden,
  children,
  className,
  isCruiseOptionPrice,
  isLightboxDisabled,
  isTaxesIncluded,
  isTooltipDisabled,
  onReady,
  price,
  priceType,
  tripInfo,
}: TProps) => {
  const loadedTripInfoRef = useRef<Record<string, unknown>>();
  useEffect(() => {
    if (!isEmpty(tripInfo) && !isEqual(tripInfo, loadedTripInfoRef.current)) {
      loadedTripInfoRef.current = { ...tripInfo };
      setTimeout(() => loadTripInfo(tripInfo), 0);
    }
  }, [tripInfo]);

  const setRef = useCallback((root: HTMLDivElement) => {
    if (!root) return;
    const observer = new MutationObserver(() => {
      const priceNode = root.querySelector('[data-up-from-currency-unit-major]') as HTMLElement;
      const priceNodeInnerText = priceNode?.innerText;
      if (priceNodeInnerText && getComputedStyle(root).display !== 'none') {
        onReady?.(priceNodeInnerText);
        observer.disconnect();
      }
    });
    observer.observe(root, { attributes: true, childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <div
      aria-label={ariaLabel}
      className={className}
      data-up-price-model={priceType === FiltersPriceType.perSailor ? 'per_person' : 'total'}
      data-up-price-type={isCruiseOptionPrice ? 'cruise_option' : 'total'}
      data-up-price-value={Math.ceil(100 * price)}
      role="button"
      tabIndex={ariaHidden ? -1 : 0}
      {...(isLightboxDisabled ? { 'data-up-disable-lightbox': true } : undefined)}
      {...(isTooltipDisabled ? { 'data-up-disable-tooltip': true } : undefined)}
      {...(isTaxesIncluded ? { 'data-up-taxes-included': true } : undefined)}
      ref={setRef}
    >
      {children}
    </div>
  );
};

export default UpliftRoot;
